import {
  DEFAULT_COLOR_GRAY,
  DEFAULT_IMAGE_HORIZONTAL,
  DEFAULT_MARKDOWN_PLACEHOLDER,
} from '../constants'
import { basePropertiesSchema } from '../base-schema-properties'
import {
  showAllSettingsSchema,
  titlePropertiesSchema,
  bgColorSchema,
  linkColorSchema,
  isBlankSchema,
  colsNumberOnDesktopSchema,
  colsNumberOnMobileSchema,
  isUseSliderOnMobileSchema,
  textFontFamilySchema,
  textWeightSchema,
  textAlignSchemaField,
  getButtonStyleProperties,
} from '../common'

const buttonStyleProperties = getButtonStyleProperties()
const buttonSettingsHidden =
  '{{parentFormData.cardType !== "text" || parentFormData.isButtonShown !== true}}'

export default {
  $schema: 'http://json-schema.org/draft-07/schema#',
  title: 'Cards with links',
  description:
    'Block with a configurable number of cards per line. It can contain link, text or image',
  type: 'object',
  required: [
    'colsNumberOnDesktop',
    'colsNumberOnMobile',
    // 'rowsNumberInSliderOnMobile', // TODO: implement rows avoiding swiper grip option
  ],
  'ui:options': {
    onlyShowIfDependent: true,
  },
  dependencies: {
    tmp_showAllSettings: [
      'colsNumberOnDesktop',
      'colsNumberOnMobile',
      'mediumColsMode',
      'colsNumberOnMedium',
      'isUseSliderOnMobile',
      'isShadowed',
      'shadowColor',
      'isBordered',
      'borderColor',
      'bgColor',
      'horizontalPadding',
      'verticalPadding',
      'linkColor',
      'textColor',
    ],
    title: ['titleAlign', 'hideOnEmbed'],
  },
  properties: {
    ...basePropertiesSchema,
    ...titlePropertiesSchema,
    ...showAllSettingsSchema,
    ...bgColorSchema,
    horizontalPadding: {
      title: 'Cards horizontal padding',
      type: 'string',
      enum: ['none', 'xs', 'sm', 'md', 'lg'],
      enumNames: ['None', 'Extra small', 'Small', 'Medium', 'Large'],
      default: 'sm',
    },
    verticalPadding: {
      title: 'Cards vertical padding',
      type: 'string',
      enum: ['none', 'xs', 'sm', 'md', 'lg'],
      enumNames: ['None', 'Extra small', 'Small', 'Medium', 'Large'],
      default: 'xs',
    },
    ...colsNumberOnDesktopSchema,
    ...colsNumberOnMobileSchema,
    mediumColsMode: {
      title: 'Columns on medium screens',
      type: 'string',
      enum: ['auto', 'manual'],
      enumNames: ['Automatic', 'Manual'],
      default: 'auto',
      'ui:widget': 'RadioWidget',
    },
    colsNumberOnMedium: {
      title: 'Number of cols on medium screens',
      type: 'integer',
      default: 3,
      minimum: 1,
      maximum: 6,
      'ui:hidden': '{{parentFormData.mediumColsMode !== "manual"}}',
    },
    ...isUseSliderOnMobileSchema,
    // rowsNumberInSliderOnMobile: {
    //   title: 'Number of rows in slider on mobile',
    //   type: 'number',
    //   default: 1,
    //   minimum: 1,
    //   maximum: 4,
    //   'ui:hidden': '{{parentFormData.isUseSliderOnMobile === false}}',
    // },
    isShadowed: {
      title: 'Add shadow',
      type: 'boolean',
      default: true,
    },
    shadowColor: {
      title: 'Shadow Color',
      type: 'string',
      default: DEFAULT_COLOR_GRAY,
      'ui:hidden': '{{parentFormData.isShadowed === false}}',
    },
    isBordered: {
      title: 'Add border',
      type: 'boolean',
      default: false,
    },
    borderColor: {
      title: 'Border Color',
      type: 'string',
      default: DEFAULT_COLOR_GRAY,
      'ui:hidden': '{{parentFormData.isBordered === false}}',
    },
    cards: {
      type: 'array',
      title: 'A list of cards',
      'ui:options': {
        showIndexNumber: true,
      },
      items: {
        title: 'Card',
        type: 'object',
        properties: {
          cardType: {
            title: 'Card type',
            type: 'string',
            enum: ['image', 'text'],
            enumNames: ['Image', 'Text'],
            default: 'image',
          },
          imageUrl: {
            title: 'Image url',
            type: 'string',
            default: DEFAULT_IMAGE_HORIZONTAL,
            'ui:hidden': '{{parentFormData.cardType !== "image"}}',
          },
          text: {
            title: 'Text',
            type: 'string',
            default: DEFAULT_MARKDOWN_PLACEHOLDER,
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          link: {
            title: 'Card link',
            type: 'string',
          },
          ...isBlankSchema,
          textColor: {
            title: 'Text Color',
            type: 'string',
            default: DEFAULT_COLOR_GRAY,
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          fontWeight: {
            ...textWeightSchema,
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          textAlign: {
            ...textAlignSchemaField,
            title: 'Text align',
            'ui:widget': 'RadioWidget',
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          textFontFamily: {
            ...textFontFamilySchema.textFontFamily,
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          linkColor: {
            ...linkColorSchema.linkColor,
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          bgColor: {
            title: 'Background Color',
            type: 'string',
            default: '',
          },
          isButtonShown: {
            title: 'Show button',
            type: 'boolean',
            default: false,
            'ui:hidden': '{{parentFormData.cardType !== "text"}}',
          },
          buttonText: {
            ...buttonStyleProperties.text,
            'ui:hidden': buttonSettingsHidden,
          },
          buttonLook: {
            ...buttonStyleProperties.look,
            'ui:hidden': buttonSettingsHidden,
          },
          buttonColor: {
            ...buttonStyleProperties.color,
            'ui:hidden': buttonSettingsHidden,
          },
          buttonColorHover: {
            ...buttonStyleProperties.colorHover,
            'ui:hidden': buttonSettingsHidden,
          },
          buttonTextColor: {
            ...buttonStyleProperties.textColor,
            'ui:hidden':
              '{{parentFormData.cardType !== "text" || parentFormData.isButtonShown !== true || parentFormData.buttonLook !== "solid"}}',
          },
          buttonTextFontFamily: {
            ...buttonStyleProperties.textFontFamily,
            'ui:hidden': buttonSettingsHidden,
          },
          buttonWidthDesktop: {
            title: 'Button width on desktop',
            type: 'string',
            enum: ['content', 'full', 'pixels'],
            enumNames: [
              'Fit content',
              'Full card width',
              'Custom width in pixels',
            ],
            default: 'content',
            'ui:widget': 'RadioWidget',
            'ui:hidden': buttonSettingsHidden,
          },
          buttonWidthDesktopPx: {
            title: 'Button width on desktop (px)',
            type: 'number',
            minimum: 1,
            default: 200,
            'ui:hidden':
              '{{parentFormData.cardType !== "text" || parentFormData.isButtonShown !== true || parentFormData.buttonWidthDesktop !== "pixels"}}',
          },
          buttonWidthMobile: {
            title: 'Button width on mobile',
            type: 'string',
            enum: ['content', 'full', 'pixels'],
            enumNames: [
              'Fit content',
              'Full card width',
              'Custom width in pixels',
            ],
            default: 'content',
            'ui:widget': 'RadioWidget',
            'ui:hidden': buttonSettingsHidden,
          },
          buttonWidthMobilePx: {
            title: 'Button width on mobile (px)',
            type: 'number',
            minimum: 1,
            default: 200,
            'ui:hidden':
              '{{parentFormData.cardType !== "text" || parentFormData.isButtonShown !== true || parentFormData.buttonWidthMobile !== "pixels"}}',
          },
          buttonAlignDesktop: {
            title: 'Button horizontal align on desktop',
            type: 'string',
            enum: ['text', 'start', 'center', 'end'],
            enumNames: ['Same as text', 'Left', 'Center', 'Right'],
            default: 'text',
            'ui:widget': 'RadioWidget',
            'ui:hidden': buttonSettingsHidden,
          },
          buttonAlignMobile: {
            title: 'Button horizontal align on mobile',
            type: 'string',
            enum: ['text', 'start', 'center', 'end'],
            enumNames: ['Same as text', 'Left', 'Center', 'Right'],
            default: 'text',
            'ui:widget': 'RadioWidget',
            'ui:hidden': buttonSettingsHidden,
          },
        },
      },
    },
  },
}
